/**
 * Géométrie déterministe des remontées mécaniques.
 *
 * À partir de (type, gare aval, gare amont) et du relief, client et serveur
 * calculent la même ligne : pylônes, brins montant et descendant, longueur,
 * nombre de véhicules. Le serveur s'en sert pour les durées de trajet, le
 * client pour dessiner pylônes, câbles, sièges et passagers.
 */
import { LIFTS, type LiftKind } from './catalog';
import { Path3, type Vec2, type Vec3 } from './geometry';
import type { Heightfield } from './terrain';
import type { CableState } from './state';

export interface Pylon {
  x: number;
  z: number;
  /** Altitude du sol. */
  ground: number;
  /** Altitude du câble. */
  cableY: number;
}

export interface LiftGeometry {
  kind: LiftKind;
  bottom: Vec2;
  top: Vec2;
  /** Direction horizontale unitaire aval -> amont. */
  dir: Vec2;
  /** Côté du brin montant (axe +X local des gares et pylônes). */
  side: Vec2;
  /** Rotation y qui aligne l'axe +Z local sur `dir`. */
  heading: number;
  groundBottom: number;
  groundTop: number;
  pylons: Pylon[];
  /** Brin montant, de l'ancrage aval à l'ancrage amont. */
  up: Path3;
  /** Brin descendant, de l'ancrage amont à l'ancrage aval. */
  down: Path3;
  length: number;
  horizontal: number;
  rise: number;
  avgSlope: number;
  loop: number;
  carriers: number;
  spacing: number;
  /** Durée nominale de la montée (s). */
  rideSeconds: number;
}

export function cableAt(cable: CableState, t: number): number {
  return cable.s + cable.v * (t - cable.t0);
}

export function computeLiftGeometry(hf: Heightfield, kind: LiftKind, bottom: Vec2, top: Vec2): LiftGeometry {
  const spec = LIFTS[kind];
  const dx = top[0] - bottom[0];
  const dz = top[1] - bottom[1];
  const horizontal = Math.hypot(dx, dz) || 1;
  const dir: Vec2 = [dx / horizontal, dz / horizontal];
  const side: Vec2 = [dir[1], -dir[0]];
  const heading = Math.atan2(dir[0], dir[1]);
  const groundBottom = hf.height(bottom[0], bottom[1]);
  const groundTop = hf.height(top[0], top[1]);

  const a: Vec3 = [bottom[0] + dir[0] * spec.stationAnchor, groundBottom + spec.stationCable, bottom[1] + dir[1] * spec.stationAnchor];
  const b: Vec3 = [top[0] - dir[0] * spec.stationAnchor, groundTop + spec.stationCable, top[1] - dir[1] * spec.stationAnchor];
  const span = Math.hypot(b[0] - a[0], b[2] - a[2]);
  const n = Math.max(0, Math.round(span / spec.pylonSpacing) - 1);

  // Abscisses (0..1) des pylônes le long de la ligne.
  let ts: number[] = [];
  for (let k = 1; k <= n; k++) ts.push(k / (n + 1));

  const pointAt = (t: number): Vec3 => {
    const x = a[0] + (b[0] - a[0]) * t;
    const z = a[2] + (b[2] - a[2]) * t;
    return [x, hf.height(x, z) + spec.pylonCable, z];
  };
  const minClear = kind === 'teleski' ? 3 : 5.5;

  // Ajoute des pylônes là où le câble passerait trop près du sol.
  for (let iter = 0; iter < 8; iter++) {
    const knots = [0, ...ts, 1];
    const ys = knots.map((t, i) => (i === 0 ? a[1] : i === knots.length - 1 ? b[1] : pointAt(t)[1]));
    let insertAt = -1;
    for (let i = 0; i < knots.length - 1 && insertAt < 0; i++) {
      for (const f of [0.25, 0.5, 0.75]) {
        const t = (knots[i] as number) + ((knots[i + 1] as number) - (knots[i] as number)) * f;
        const x = a[0] + (b[0] - a[0]) * t;
        const z = a[2] + (b[2] - a[2]) * t;
        const cy = (ys[i] as number) + ((ys[i + 1] as number) - (ys[i] as number)) * f;
        if (cy - hf.height(x, z) < minClear) {
          insertAt = i;
          break;
        }
      }
    }
    if (insertAt < 0) break;
    const mid = ((knots[insertAt] as number) + (knots[insertAt + 1] as number)) / 2;
    ts = [...ts, mid].sort((p, q) => p - q);
  }

  const pylons: Pylon[] = ts.map((t) => {
    const p = pointAt(t);
    return { x: p[0], z: p[2], ground: p[1] - spec.pylonCable, cableY: p[1] };
  });

  const centre: Vec3[] = [a, ...pylons.map((p): Vec3 => [p.x, p.cableY, p.z]), b];
  const off = spec.cableOffset;
  const upPts = centre.map((p): Vec3 => [p[0] + side[0] * off, p[1], p[2] + side[1] * off]);
  const downPts = centre
    .slice()
    .reverse()
    .map((p): Vec3 => [p[0] - side[0] * off, p[1], p[2] - side[1] * off]);
  const up = new Path3(upPts);
  const down = new Path3(downPts);
  const loop = up.length + down.length;
  const nominal = spec.speed * spec.interval;
  const carriers = Math.max(2, Math.round(loop / nominal));
  const spacing = loop / carriers;
  const rise = groundTop - groundBottom;

  return {
    kind,
    bottom,
    top,
    dir,
    side,
    heading,
    groundBottom,
    groundTop,
    pylons,
    up,
    down,
    length: up.length,
    horizontal,
    rise,
    avgSlope: rise / horizontal,
    loop,
    carriers,
    spacing,
    rideSeconds: up.length / spec.speed,
  };
}

/**
 * Position d'un point de la boucle de câble (abscisse p dans [0, loop)).
 * Écrit [x, y, z, dx, dz] dans out ; renvoie vrai sur le brin montant.
 */
export function sampleLoop(geo: LiftGeometry, p: number, out: Float64Array | number[]): boolean {
  let q = p % geo.loop;
  if (q < 0) q += geo.loop;
  if (q < geo.up.length) {
    geo.up.sample(q, out);
    return true;
  }
  geo.down.sample(q - geo.up.length, out);
  return false;
}

/** Point d'embarquement (au sol) d'une remontée. */
export function boardingPoint(geo: LiftGeometry): Vec2 {
  const spec = LIFTS[geo.kind];
  return [geo.bottom[0] + geo.dir[0] * spec.stationAnchor + geo.side[0] * spec.cableOffset, geo.bottom[1] + geo.dir[1] * spec.stationAnchor + geo.side[1] * spec.cableOffset];
}

/** Point de sortie en haut (au sol), côté brin montant. */
export function topExit(geo: LiftGeometry): Vec2 {
  return [geo.top[0] + geo.side[0] * 9 + geo.dir[0] * 3, geo.top[1] + geo.side[1] * 9 + geo.dir[1] * 3];
}

/** Place n° index de la file d'attente (derrière la gare aval) : [x, z]. */
export function queueSlot(geo: LiftGeometry, index: number): Vec2 {
  const cols = geo.kind === 'teleski' ? 2 : 6;
  const row = Math.floor(index / cols);
  const col = index % cols;
  const back = (geo.kind === 'teleski' ? 4 : 9) + row * 1.6;
  const lateral = (col - (cols - 1) / 2) * 1.25 + LIFTS[geo.kind].cableOffset;
  return [geo.bottom[0] - geo.dir[0] * back + geo.side[0] * lateral, geo.bottom[1] - geo.dir[1] * back + geo.side[1] * lateral];
}

/** Entrée de la file (là où un skieur la rejoint). */
export function queueEntry(geo: LiftGeometry, length: number): Vec2 {
  return queueSlot(geo, Math.max(0, length));
}
